export const withResolvers = <T>() => {
  let resolve!: (value: T) => void;
  let reject!: (reason?: unknown) => void;

  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });

  return {
    promise,
    resolve,
    reject,
  };
};

export type TrackedPromise<T> = Promise<T> & {
  _resolved: boolean;
  _value?: T;
  _error?: Error;
};

export const withTrackedResolvers = <T>() => {
  const { promise, resolve, reject } = withResolvers<T>();

  const tracked = promise as TrackedPromise<T>;

  tracked._resolved = false;
  tracked._value = undefined;
  tracked._error = undefined;

  const trackedResolve = (value: T) => {
    tracked._resolved = true;
    tracked._value = value;
    resolve(value);
  };

  const trackedReject = (error: Error) => {
    tracked._resolved = true;
    tracked._error = error;
    reject(error);
  };

  return { promise: tracked, resolve: trackedResolve, reject: trackedReject };
};

export const selectTrackedPromise = <T>(
  source: TrackedPromise<T>,
  selectedValue: T
): TrackedPromise<T> => {
  const wrapper = new Promise((resolve, reject) => {
    source.then(resolve, reject).catch(reject);
  }) as TrackedPromise<T>;
  wrapper._resolved = source._resolved;
  wrapper._value = selectedValue;
  wrapper._error = source._error;
  return wrapper;
};
